import smartSolveApi from './instances/smartsolve';
import { handleErrors } from '../actions/ErrorActions';
import { CACHE_DB } from '../constants/indexedDB.constants';
import { downloadFile } from '../utils/domUtils';
import {
  getCacheDBValueByKey,
  setCacheDBValue,
  shouldSetCacheDBValue,
} from '../utils/indexedDB.cache.utils';

const {
  OBJECT_STORES: { DETAIL_FORM_MODELS },
  KEY_PATHS: { FORM_KEY },
} = CACHE_DB;

export const getFormModel = async ({
  formKey = '',
  formId,
  orgId,
  containerWorkflowKey,
  workflowKey,
  workflowId,
  stepId,
  itmId,
  forEdit = false,
  isCreateForm,
  newFormParams,
}) => {
  try {
    const cacheKey = `${formKey} + ${localStorage.getItem('userCacheKey')}`;
    const cachedData = forEdit
      ? await Promise.resolve(null)
      : await getCacheDBValueByKey(DETAIL_FORM_MODELS, cacheKey);

    if (cachedData) {
      return cachedData.cache;
    }

    let response = {};

    if (isCreateForm) {
      newFormParams.formEntity = `EQM.Biz.Generic.Components|GenericRecords|ClientEnableForm|EQM.UI.Web.ssForms.GenericFormControl4|${newFormParams.formEntity}`;
      response = await smartSolveApi.post('/forms/opentemplate', newFormParams);
    } else {
      response = await smartSolveApi.post('/forms/getformmodel', {
        formKey,
        formId,
        orgId,
        containerWorkflowKey,
        workflowKey,
        workflowId,
        stepId,
        itmId,
        forEdit,
      });
    }

    const lastModifiedDate = response.data.formDefinition?.formInfo?.opsModifiedDate;

    if (
      !response.data.hasError &&
      !forEdit &&
      shouldSetCacheDBValue(DETAIL_FORM_MODELS, formKey, lastModifiedDate)
    ) {
      const detailFormCacheDays = +localStorage.getItem('detailFormCacheDays');
      const { forms } = JSON.parse(localStorage.getItem('uiCacheObjects') || '{}');

      await setCacheDBValue(DETAIL_FORM_MODELS, {
        [FORM_KEY]: cacheKey,
        cache: response.data,
        modifiedDate: forms[formKey],
        cacheExpirationDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * detailFormCacheDays),
      });
    }

    return response.data;
  } catch (e) {
    throw new Error(`getFormModel() failed: ${e}`);
  }
};

export const saveFormDefinitions = async ({ formDefinition }) => {
  try {
    const response = await smartSolveApi.post('/forms/save', formDefinition);

    return response.data;
  } catch (e) {
    throw new Error(`saveFormDefinitions() failed: ${e}`);
  }
};

export const unpublishForm = async (formKey) => {
  try {
    const { data } = await smartSolveApi.post('/forms/unpublish', { formKey });

    return data;
  } catch (e) {
    throw new Error(`unpublishForm() failed: ${e}`);
  }
};

export const downloadFieldsToFormMapping = async (formEntityKey, dispatch) => {
  try {
    const { data } = await smartSolveApi.post('/entityfieldstoformmapping ', { formEntityKey });

    if (data.hasError) {
      return dispatch(handleErrors(data.errors));
    }

    downloadFile(`${formEntityKey}_FieldsToFormMapping.txt`, data, 1);

    return data;
  } catch (e) {
    throw new Error(`downloadFieldsToFormMapping() failed: ${e}`);
  }
};

export const downloadDeltaChanges = async (requestBody) => {
  try {
    const response = await smartSolveApi.post('forms/createdeltadocument', requestBody);
    downloadFile(`${requestBody.formKey}_DeltaChanges.html`, response.data, 1);

    return response.data;
  } catch (e) {
    throw new Error(`downloadDeltaChanges() failed: ${e}`);
  }
};

export const downloadMergedDeltaChanges = async (requestBody) => {
  try {
    const response = await smartSolveApi.post('forms/createdeltamergeddocument', requestBody);
    downloadFile(`${requestBody.formKey}_MergedDeltaChanges.html`, response.data, 1);

    return response.data;
  } catch (e) {
    throw new Error(`downloadMergedDeltaChanges() failed: ${e}`);
  }
};

export const downloadFormRules = async (requestBody) => {
  try {
    const response = await smartSolveApi.post('forms/extractrules', requestBody);
    downloadFile(`${requestBody.formKey}_FormRules.txt`, response.data, 1);

    return response.data;
  } catch (e) {
    throw new Error(`downloadFormRules() failed: ${e}`);
  }
};

export const downloadAllFormsRules = async () => {
  try {
    const response = await smartSolveApi.post('forms/extractrules', {});
    downloadFile('formRules.txt', response.data, 1);

    return response.data;
  } catch (e) {
    throw new Error(`downloadAllFormsRules() failed: ${e}`);
  }
};

export const deleteForm = async (requestBody) => {
  try {
    const response = await smartSolveApi.post('forms/delete', requestBody);

    return response.data;
  } catch (e) {
    throw new Error(`deleteForm() failed: ${e}`);
  }
};

export const getEntityCatalog = async ({ entityKey = '' } = {}) => {
  try {
    const response = await smartSolveApi.post('forms/entitycatalog', {
      entityKey,
    });

    return response.data;
  } catch (e) {
    throw new Error(`getEntityCatalog() failed: ${e}`);
  }
};

export const getListOptions = async ({ entityKey = '' }) => {
  try {
    const response = await smartSolveApi.post('forms/listoptions', {
      entityKey,
    });

    return response.data;
  } catch (e) {
    throw new Error(`getListOptions() failed: ${e}`);
  }
};

export const getDatasourceFields = async ({ entityKey = '', dataLookupType = '' }) => {
  try {
    const response = await smartSolveApi.post('forms/datasourcefields', {
      entityKey,
      dataLookupType,
    });

    return response.data;
  } catch (e) {
    throw new Error(`getDatasourceFields() failed: ${e}`);
  }
};

export const getExternalDatasources = async () => {
  try {
    const response = await smartSolveApi.post('forms/externaldatasources', {});
    return response.data;
  } catch (e) {
    throw new Error(`getExternalDatasources() failed: ${e}`);
  }
};

export const getFormListByEntityKey = async (entityKey) => {
  try {
    const response = await smartSolveApi.post('forms/formlistbyentity', {
      entityKey,
      includeSubForms: true,
    });
    return response.data;
  } catch (e) {
    throw new Error(`getFormListByEntityKey() failed: ${e}`);
  }
};

export const verifyFormExists = async (params) => {
  try {
    const response = await smartSolveApi.post('/forms/verifyformexists', params);
    return response.data.formExists;
  } catch (e) {
    throw new Error(`verifyFormExists() failed: ${e}`);
  }
};

export const importForm = async (file) => {
  try {
    const formData = new FormData();

    formData.append('FileName', file.name);
    formData.append('FileStream', file);

    const response = await smartSolveApi.post('/forms/import', formData);
    return response.data;
  } catch (error) {
    throw new Error(`importForm() failed: ${error}`);
  }
};
